// Collaborative cursors i minimap: visa var andra coop-peers tittar/redigerar.
// Pure helper för koord-transformation + cursor-TTL. UI-lagret renderar
// bara en cirkel per peer utanför detta.

export interface PeerCursor {
  userId: string;
  userName: string;
  color: string;
  /** Viewport center i canvas-koord. */
  x: number;
  y: number;
  lastSeenAt: number;
}

export interface MinimapBounds {
  canvasWidth: number;
  canvasHeight: number;
  minimapWidth: number;
  minimapHeight: number;
  offsetX?: number;
  offsetY?: number;
}

export interface ProjectedCursor {
  userId: string;
  userName: string;
  color: string;
  /** I minimap-koord (pixlar). */
  minimapX: number;
  minimapY: number;
  opacity: number;
}

/**
 * Projicera canvas-koord → minimap-koord. Scale = minimap/canvas ratio.
 */
export function projectToMinimap(cursor: PeerCursor, bounds: MinimapBounds): {
  minimapX: number;
  minimapY: number;
} {
  const scaleX = bounds.minimapWidth / Math.max(1, bounds.canvasWidth);
  const scaleY = bounds.minimapHeight / Math.max(1, bounds.canvasHeight);
  return {
    minimapX: (bounds.offsetX ?? 0) + cursor.x * scaleX,
    minimapY: (bounds.offsetY ?? 0) + cursor.y * scaleY,
  };
}

/**
 * Filtrera + projicera aktiva peer-cursors. Cursors som ej setts på
 * `staleAfterMs` ms gömmer helt; cursors inom `fadeAfterMs` fade:ar
 * mot 0 opacity för visuell feedback.
 */
export function activeCursors(
  peers: PeerCursor[],
  bounds: MinimapBounds,
  now: number,
  opts: { staleAfterMs?: number; fadeAfterMs?: number } = {},
): ProjectedCursor[] {
  const stale = opts.staleAfterMs ?? 30_000;
  const fade = opts.fadeAfterMs ?? 5_000;
  const out: ProjectedCursor[] = [];
  for (const p of peers) {
    const age = now - p.lastSeenAt;
    if (age > stale) continue;
    const { minimapX, minimapY } = projectToMinimap(p, bounds);
    const opacity = age <= fade ? 1 : Math.max(0.1, 1 - (age - fade) / (stale - fade));
    out.push({
      userId: p.userId,
      userName: p.userName,
      color: p.color,
      minimapX,
      minimapY,
      opacity,
    });
  }
  return out;
}

/**
 * Stable färg-hash per userId så samma peer alltid får samma cursor-färg
 * cross-sessions (om client inte picka:t en själv).
 */
export function deriveCursorColor(userId: string): string {
  const palette = [
    '#ef4444', '#f59e0b', '#10b981', '#3b82f6',
    '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16',
  ];
  let hash = 0;
  for (let i = 0; i < userId.length; i += 1) {
    hash = (hash * 31 + userId.charCodeAt(i)) | 0;
  }
  return palette[Math.abs(hash) % palette.length];
}
